import { ArrowRight, Scale } from "lucide-react";
import { Button } from "../components/ui/button";

const HERO_BG = "./images/hero-bg.png";

export default function HeroSection() {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[92vh] flex items-center justify-center overflow-hidden isolate"
    >
      {/* Background Image with slow Ken Burns zoom */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat hero-kenburns"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      {/* Overlay */}
      <div className="absolute inset-0 hero-overlay" />
      {/* Vignette for depth */}
      <div className="absolute inset-0 hero-vignette pointer-events-none" />

      {/* Aurora glow orbs */}
      <div
        className="aurora-orb delay-1"
        style={{
          width: "560px",
          height: "560px",
          top: "-8%",
          left: "12%",
          background: "radial-gradient(circle, rgba(184,153,104,0.45), rgba(184,153,104,0) 70%)",
        }}
      />
      <div
        className="aurora-orb delay-2"
        style={{
          width: "680px",
          height: "680px",
          bottom: "-14%",
          right: "8%",
          background: "radial-gradient(circle, rgba(224,194,154,0.35), rgba(224,194,154,0) 70%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center py-32">
        {/* Overline — glass pill */}
        <div className="animate-fade-in-up opacity-0 animation-delay-100">
          <div className="inline-flex items-center gap-2 mb-8 px-5 py-2 border border-[#b89968]/30 rounded-full bg-white/5 backdrop-blur-md shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
            <Scale className="w-4 h-4 gold-accent" />
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-[#e0d5c0]">
              Advocates &amp; Legal Consultants
            </span>
          </div>
        </div>

        {/* Main Title with sheen sweep */}
        <div className="animate-fade-in-up opacity-0 animation-delay-200">
          <h1
            data-testid="hero-title"
            className="font-heading text-5xl sm:text-6xl lg:text-[7.5rem] font-bold tracking-tight leading-none mb-4 sheen-text"
          >
            Corpus Juris
          </h1>
        </div>
        <div className="animate-fade-in-up opacity-0 animation-delay-300">
          <p className="text-lg sm:text-xl lg:text-2xl font-light tracking-[0.32em] uppercase shimmer-text mb-4">
            Law Chambers
          </p>
        </div>

        {/* Divider */}
        <div className="animate-fade-in-up opacity-0 animation-delay-300">
          <div className="section-gold-line mx-auto my-8" />
        </div>

        {/* Lawyer Name */}
        <div className="animate-fade-in-up opacity-0 animation-delay-400">
          <p className="text-xl sm:text-2xl text-white font-heading font-medium italic mb-2">
            Adv. Akshay Dominic V
          </p>
          <p className="text-sm text-[#b89968]/80 tracking-widest uppercase">
            BBA LLB (Hons) &mdash; Chavakkad, Thrissur, Kerala
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12 animate-fade-in-up opacity-0 animation-delay-500">
          <Button
            data-testid="hero-cta"
            onClick={() => scrollTo("#contact")}
            className="glossy-btn rounded-full bg-[#b89968] text-[#081226] hover:bg-[#e0c29a] px-10 py-3.5 text-sm font-semibold flex items-center gap-2 h-auto shadow-xl shadow-[#b89968]/30 transition-all hover:shadow-2xl hover:shadow-[#b89968]/40 tracking-wide"
          >
            Book a Consultation
            <ArrowRight className="w-4 h-4" />
          </Button>
          <Button
            data-testid="hero-practice-btn"
            variant="outline"
            onClick={() => scrollTo("#practice")}
            className="rounded-full border-white/25 bg-white/5 backdrop-blur-md text-white hover:bg-white/10 hover:border-[#b89968]/60 px-10 py-3.5 text-sm font-medium h-auto transition-all tracking-wide"
          >
            Our Practice Areas
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-8 mt-20 max-w-lg mx-auto animate-fade-in-up opacity-0 animation-delay-600">
          {[
            { num: "5+", label: "Years Experience" },
            { num: "500+", label: "Cases Handled" },
            { num: "9+", label: "Practice Areas" },
          ].map((s) => (
            <div key={s.label}>
              <p className="font-heading text-4xl lg:text-5xl font-bold gold-accent">{s.num}</p>
              <p className="text-[11px] text-white/55 mt-2 tracking-[0.18em] uppercase">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom gradient fade to surface */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#f7f3ea] to-transparent" />

      {/* Scroll hint */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#b89968]/60 animate-fade-in opacity-0 animation-delay-700">
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-[#b89968]/60 to-transparent" />
      </div>
    </section>
  );
}
